import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../components';
import { useAuth } from '../../hooks';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EastIcon from '@mui/icons-material/East';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RequestPageIcon from '@mui/icons-material/RequestPage';

const ApplicationsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const allSettingsCards = [
    {
      id: 'leaveRequests',
      title: t('applications.titles.leaveRequests'),
      description: t('applications.descriptions.leaveRequests'),
      icon: <AssignmentTurnedInIcon sx={{ fontSize: 28 }} />,
      path: '/applications/leave-requests',
      color: theme.palette.warning.main,
    },
    {
      id: 'expenses',
      title: t('applications.titles.expenses'),
      description: t('applications.descriptions.expenses'),
      icon: <ReceiptLongIcon sx={{ fontSize: 28 }} />,
      path: '/applications/expenses',
      color: theme.palette.primary.main,
    },
    {
      id: 'userFirms',
      title: t('applications.titles.userFirms'),
      description: t('applications.descriptions.userFirms'),
      icon: <GroupWorkIcon sx={{ fontSize: 28 }} />,
      path: '/applications/user-firms',
      color: theme.palette.success.main,
    },
    {
      id: 'tahsilat',
      title: t('applications.titles.tahsilat', 'Tahsilat Yönetimi'),
      description: t('applications.descriptions.tahsilat', 'Luca raporlarını import edin, tahsilatları takip edin.'),
      icon: <AccountBalanceIcon sx={{ fontSize: 28 }} />,
      path: '/applications/tahsilat',
      color: theme.palette.info.main,
    },
    {
      id: 'ciroTalepleri',
      title: t('applications.titles.ciroTalepleri', 'Ciro Talepleri'),
      description: t(
        'applications.descriptions.ciroTalepleri',
        'Kullanıcıların gönderdiği ciro kayıtlarını onayla veya reddet.'
      ),
      icon: <RequestPageIcon sx={{ fontSize: 28 }} />,
      path: '/applications/ciro-talepleri',
      color: theme.palette.secondary.main,
      requiresRoot: true,
    },
    {
      id: 'dynamicReports',
      title: t('applications.titles.dynamicReports', 'Dinamik Raporlar'),
      description: t(
        'applications.descriptions.dynamicReports',
        'Ne istediğinizi yazın; sistem sorguyu hazırlasın, siz onaylayın.'
      ),
      icon: <AutoAwesomeIcon sx={{ fontSize: 28 }} />,
      path: '/applications/dynamic-reports',
      color: theme.palette.error.main,
      requiresRoot: true,
    },
  ];

  const applicationsCards = allSettingsCards.filter(card => {
    if (card.requiresRoot && user?.role !== 'ROOT') {
      return false;
    }
    return true;
  });

  const handleCardClick = card => {
    navigate(card.path);
  };

  return (
    <PageContainer currentPage={t('pages.applications')}>
      <Box sx={{ p: { xs: 2, md: 4 }, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('applications.titles.main')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('applications.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ px: { xs: 2, md: 3 }, pb: 4 }}>
        {applicationsCards.map(card => (
          <Grid size={{ xs: 12, sm: 4 }} key={card.id} sx={{ display: 'flex' }}>
            <Card
              onClick={() => handleCardClick(card)}
              elevation={0}
              sx={{
                width: '100%',
                cursor: 'pointer',
                borderRadius: 3,
                border: `1.5px solid ${theme.palette.divider}`,
                background: theme.palette.background.paper,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: card.color,
                  boxShadow: `0 12px 32px ${card.color}22`,
                  '& .AppCard-Icon': {
                    transform: 'scale(1.1)',
                  },
                  '& .AppCard-Arrow': {
                    transform: 'translateX(4px)',
                    opacity: 1,
                  },
                },
              }}
            >
              {/* Renkli üst çizgi */}
              <Box sx={{ height: 4, backgroundColor: card.color, flexShrink: 0 }} />

              <CardContent
                sx={{
                  p: 3,
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {/* İkon alanı */}
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    backgroundColor: `${card.color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: card.color,
                  }}
                >
                  <Box
                    className="AppCard-Icon"
                    sx={{ display: 'flex', transition: 'transform 0.2s ease' }}
                  >
                    {card.icon}
                  </Box>
                </Box>

                {/* Başlık + Açıklama */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {card.description}
                  </Typography>
                </Box>

                {/* Alt aksiyon satırı */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: 1.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: card.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}
                  >
                    {t('applications.manage')}
                  </Typography>
                  <EastIcon
                    className="AppCard-Arrow"
                    sx={{ fontSize: 16, color: card.color, opacity: 0.6, transition: 'transform 0.2s ease, opacity 0.2s ease' }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </PageContainer>
  );
};

export default ApplicationsPage;
